import PageLayout from "@/components/feature/PageLayout";

const sectionClass = "flex flex-col gap-3";
const headingClass = "font-fraunces text-[#2B2B2B] text-xl font-medium";
const linkClass = "text-[#3C6FF8] hover:underline";

export default function PrivacyPolicy() {
  return (
    <PageLayout>
      <section className="w-full py-16 md:py-24">
        <article className="w-full px-6 md:px-12 lg:px-20 max-w-3xl mx-auto">
          <h1 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-4">
            Privacy Policy
          </h1>
          <p className="font-manrope text-[#2B2B2B]/60 text-sm mb-12">
            Last updated: October 3, 2026
          </p>

          <div className="flex flex-col gap-9 font-manrope text-[15px] text-[#2B2B2B]/80 leading-relaxed">
            <div className={sectionClass}>
              <p>
                Perspective Therapy Mental Health Counseling, PLLC ("Perspective Therapy," "we," "us," or "our") respects the privacy of visitors to perspectivetherapy.com. This policy explains how we handle information through our public website and related website communications.
              </p>
              <p>
                This policy does not govern clinical records or information handled through an authenticated client portal. Those systems and therapy services may be subject to separate notices, agreements, and clinical consent documents.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Information We Collect</h2>
              <p>
                You may provide your name, email address, telephone number, the service you are interested in, a message, or other information when you submit a contact or inquiry form, contact us by email or telephone, interact with the website concierge, or otherwise communicate with us. Please do not submit sensitive clinical details through the public website or its chat feature.
              </p>
              <p>
                If you subscribe to blog updates, we collect the email address you submit and information about when the subscription request was made. Clients may separately provide a mobile number and opt in to appointment-reminder text messages through the client portal or another authorized process.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Cookies and Analytics</h2>
              <p>
                Like most hosted websites, our website infrastructure may process standard technical information needed to deliver and protect the site, such as an IP address, browser or device type, requested pages, request date and time, and error or security logs. The site may also store a language preference in your browser so it can remember that setting.
              </p>
              <p>
                We use necessary browser storage to remember privacy preferences and support essential website functionality. With your permission, we use Google Analytics and Google Ads measurement to measure aggregate page visits, traffic sources, general engagement, and whether advertising campaigns lead to website activity. Optional measurement is off until you consent and does not receive the contents of forms or clinical information through our implementation. We do not use these tools for remarketing, Customer Match, personalized advertising, or to send user-provided contact or health information to Google.
              </p>
              <p>
                You can accept or reject optional analytics and ad measurement in the cookie banner and change your choice later using <strong>Cookie Preferences</strong> in the website footer. Rejecting optional measurement does not limit access to the website, contact forms, blog subscriptions, or therapy services.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>How We Use Information</h2>
              <p>We may use information to:</p>
              <ul className="list-disc pl-5 flex flex-col gap-1">
                <li>respond to inquiries, questions, and requests;</li>
                <li>support scheduling and other administrative communications;</li>
                <li>operate, maintain, secure, troubleshoot, and improve the public website;</li>
                <li>provide requested services and communications, including blog updates;</li>
                <li>send automated appointment reminders to clients who separately opt in to SMS;</li>
                <li>prevent misuse and protect our rights, visitors, and systems; and</li>
                <li>comply with applicable legal obligations.</li>
              </ul>
              <p>
                Consent to optional blog emails, consent to SMS reminders, authorization for necessary account or transactional communications, and consent to treatment are separate. Agreeing to one does not enroll you in or constitute consent to the others.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Email and Blog Subscriptions</h2>
              <p>
                You may voluntarily subscribe to receive Perspective Therapy blog updates and related requested content by email. Subscription is optional, email frequency may vary, and subscribing is not a condition of receiving therapy services. You may unsubscribe at any time using the unsubscribe mechanism provided in those emails. Unsubscribing from optional content does not affect access to therapy services or necessary transactional or account communications that are separately authorized or appropriate.
              </p>
              <p>
                Optional public website content emails are not clinical communications. We do not sell subscriber email addresses or disclose them to third parties for their own marketing.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>SMS and Mobile Information</h2>
              <p>
                If you provide your mobile phone number and consent to receive SMS messages from Perspective Therapy, we may use your number to send appointment reminders, scheduling updates, and other administrative communications. SMS consent is optional and is not a condition of receiving services or accessing the client portal. Message frequency may vary. Message and data rates may apply.
              </p>
              <p>
                Reply <strong>STOP</strong> to opt out or <strong>HELP</strong> for assistance. Where available, clients may also manage SMS reminder preferences through the client portal. Opting out of SMS does not affect therapy services or enroll or unenroll a client in any email subscription.
              </p>
              <p>
                Mobile phone contact information and SMS opt-in consent data will not be sold or rented. This information will not be shared or disclosed to third parties for marketing or promotional purposes. We may disclose it to service providers only as necessary to provide messaging or related operational services on behalf of Perspective Therapy and subject to applicable privacy and security requirements.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Service Providers</h2>
              <p>
                We may use service providers to support website hosting and operations, form processing, data storage, analytics, payment processing where applicable, email delivery, and SMS delivery. They may receive only information reasonably necessary to perform services for us and are not authorized by us to use contact, subscriber, or messaging information for their own marketing.
              </p>
              <p>
                We may also disclose information when reasonably necessary to comply with law or legal process, protect rights or safety, investigate misuse, or complete a business reorganization or transfer subject to appropriate safeguards.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Data Retention</h2>
              <p>
                We retain public website information for as long as reasonably necessary for the purposes described in this policy, including responding to requests, maintaining appropriate records, resolving disputes, and meeting legal obligations. Retention periods may vary by the type of information and reason it was collected.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Data Security</h2>
              <p>
                We use reasonable administrative, technical, and organizational safeguards designed to protect information. However, no internet transmission or storage method can be guaranteed to be completely secure. Consider the sensitivity of information before sending it through a public website, email, or text message.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>User Choices and Opt-Outs</h2>
              <p>
                You may reply <strong>STOP</strong> to opt out of SMS messages and <strong>HELP</strong> for assistance. You may accept or reject optional analytics and ad measurement through the cookie banner and change that selection at any time using <strong>Cookie Preferences</strong> in the footer. You may also contact Perspective Therapy using the details below with privacy questions or requests.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Children's Privacy</h2>
              <p>
                Perspective Therapy provides therapy services involving minors, with appropriate involvement and authorization from a parent or legal guardian. The public website is intended to provide general information and is not designed for children to independently submit personal information. A parent or legal guardian should make website inquiries concerning a child and should avoid including sensitive clinical details in a public website form or chat. Information related to therapy for a minor is handled through the appropriate clinical process, not as ordinary public-website data.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>External Sites</h2>
              <p>
                Our website links to external websites and services, including the client portal and booking services. Their privacy practices are governed by their own notices. Review those notices before providing information to an external service.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Changes to This Privacy Policy</h2>
              <p>
                We may update this policy to reflect changes to the website, our practices, or applicable requirements. We will post the revised policy here and update the date above.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Contact Information</h2>
              <p>Questions about this policy may be directed to:</p>
              <address className="not-italic">
                Perspective Therapy Mental Health Counseling, PLLC<br />
                4 Gilder Street, Suite 2<br />
                Larchmont, NY 10538<br />
                <a className={linkClass} href="mailto:Info@perspectivetherapyNY.com">Info@perspectivetherapyNY.com</a><br />
                <a className={linkClass} href="tel:+18389108150">(838) 910-8150</a>
              </address>
            </div>
          </div>
        </article>
      </section>
    </PageLayout>
  );
}
